import { QueryKey } from '@tanstack/react-query';

export type TradeQueryContext = {
  uid: string;
  marketAddress: string;
};

const getQueryType = (queryKey: QueryKey) => {
  if (typeof queryKey[0] === 'string') return queryKey[0];
  if (typeof queryKey[1] === 'string') return queryKey[1];
  return undefined;
};

export const shouldRefetchTradeQuery = (queryKey: QueryKey, ctx: TradeQueryContext) => {
  const queryType = getQueryType(queryKey);

  if (queryType === 'SignedUserAssets') {
    return queryKey[2] === ctx.uid;
  }

  // Header wallet balance. Its key holds the signer EOA, not the uid; only the
  // signed-in wallet has an active entry.
  if (queryType === 'SingerTokenBalance') return true;

  // The trade panel's CTF position read and its own balance read (only while the
  // header read fails). Their keys hold the signer EOA too; they poll every 60 s.
  if (queryType === 'OnChainPosition' || queryType === 'OnChainBalance') return true;

  // The event page trades list. Its key holds the market address, not a params
  // object. Case-insensitive, like the SSE listener of the list.
  if (queryType === 'EventMarketTrades') {
    const address = queryKey[1];
    return typeof address === 'string' && address.toLowerCase() === ctx.marketAddress?.toLowerCase();
  }

  const params = queryKey[2];
  if (!params || typeof params !== 'object') return false;

  const searchParams = params as Record<string, unknown>;

  if (queryType === 'UserPositions') {
    // Tolerate an unscoped positions query (no marketAddress, e.g. a portfolio
    // view) so it still refetches after a trade. dpin's TradeQueryContext has no
    // baseTokenAddress dimension (its currency lens diverged from DID), so that
    // condition from the source is intentionally omitted.
    return (
      searchParams.uid === ctx.uid &&
      (searchParams.marketAddress === undefined || searchParams.marketAddress === ctx.marketAddress)
    );
  }

  if (queryType === 'MarketDetail' || queryType === 'MarketTrades') {
    return searchParams.marketAddress === ctx.marketAddress;
  }

  // Refetch trade history so deal_price updates after block-listener processes the swap
  if (queryType === 'UserTrades') {
    return searchParams.uid === ctx.uid;
  }

  return false;
};
